declare const google: any;
import { GOOGLE_CONFIG } from './app-config';
import { googleSheetsService } from './google-sheets-service';

type AuthState = {
  authorized: boolean;
  token: string | null;
  error: string | null;
};

type AuthListener = (state: AuthState) => void;

class GoogleAuthService {
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private tokenClient: google.accounts.oauth2.TokenClient | null = null;
  private accessToken: string | null = null;
  private lastError: string | null = null;
  private listeners = new Set<AuthListener>();

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      await googleSheetsService.initialize();

      const gis = (window as any).google?.accounts?.oauth2;
      if (!gis) {
        throw new Error('Google Identity Services script failed to load.');
      }

      this.tokenClient = gis.initTokenClient({
        client_id: GOOGLE_CONFIG.clientId,
        scope: GOOGLE_CONFIG.scope,
        callback: (response: google.accounts.oauth2.TokenResponse) => {
          if (response.error) {
            this.handleAuthError(response.error);
            return;
          }
          this.handleToken(response.access_token);
        }
      });

      this.initialized = true;
      this.notify();
    })();

    return this.initPromise;
  }

  private handleToken(token: string) {
    this.accessToken = token;
    this.lastError = null;
    googleSheetsService.setAccessToken(token);
    this.notify();
  }

  private handleAuthError(error: string) {
    console.error('[GoogleAuth] Authorization error:', error);
    this.lastError = error;
    this.notify();
  }

  private notify() {
    const state: AuthState = {
      authorized: this.isAuthorized(),
      token: this.accessToken,
      error: this.lastError
    };
    this.listeners.forEach(listener => listener(state));
  }

  onAuthStateChange(listener: AuthListener): () => void {
    this.listeners.add(listener);
    listener({
      authorized: this.isAuthorized(),
      token: this.accessToken,
      error: this.lastError
    });
    return () => this.listeners.delete(listener);
  }

  async signIn(prompt?: 'consent' | ''): Promise<void> {
    await this.initialize();

    if (!this.tokenClient) {
      throw new Error('Token client not initialized');
    }

    return new Promise((resolve, reject) => {
      this.tokenClient!.callback = (response: google.accounts.oauth2.TokenResponse) => {
        if (response.error) {
          this.handleAuthError(response.error);
          reject(new Error(response.error));
          return;
        }
        this.handleToken(response.access_token);
        resolve();
      };

      try {
        const requestPrompt = prompt ?? (this.accessToken ? '' : 'consent');
        this.tokenClient!.requestAccessToken({ prompt: requestPrompt });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        this.handleAuthError(message);
        reject(error as Error);
      }
    });
  }

  async signOut(): Promise<void> {
    if (!this.accessToken) {
      return;
    }

    const token = this.accessToken;
    return new Promise(resolve => {
      google.accounts.oauth2.revoke(token, () => {
        this.accessToken = null;
        this.lastError = null;
        googleSheetsService.setAccessToken(null);
        this.notify();
        resolve();
      });
    });
  }

  isAuthorized(): boolean {
    return !!this.accessToken;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getLastError(): string | null {
    return this.lastError;
  }
}

export const googleAuthService = new GoogleAuthService();